import { Button, Checkbox } from '@bb/ui';
import { useState } from 'react';
import { addChannel, CHANNEL_LABELS, issuesAt, removeChannel, validateMessagesDraft, variantsWithContent, type DeliveryChannel, type MessagesDraft } from './messagesDraft.ts';

const GROUPS: Array<{ legend?: string; channels: Array<{ channel: DeliveryChannel; label: string }> }> = [
  {
    legend: 'Push',
    channels: [
      { channel: 'push_android', label: 'Android' },
      { channel: 'push_ios', label: 'iOS' },
      { channel: 'push_web', label: 'Web' },
    ],
  },
  { channels: [{ channel: 'content_card', label: 'Content Card' }] },
  { channels: [{ channel: 'in_app', label: 'In-app message' }] },
  { channels: [{ channel: 'webhook', label: 'Webhook' }] },
];

export interface ChannelsPickerProps {
  value: MessagesDraft;
  onChange: (next: MessagesDraft) => void;
  /** A launched revision: nothing can change. */
  readOnly?: boolean;
}

/**
 * Delivery channels of a campaign (BB-98). One campaign may combine channels, e.g. Android + iOS + Web push
 * (VCG «Signal New»). Every variant gets content for every selected channel; removing a channel whose content is
 * filled in asks first.
 */
export function ChannelsPicker({ value, onChange, readOnly = false }: ChannelsPickerProps) {
  const [pending, setPending] = useState<DeliveryChannel | null>(null);
  const confirming = pending !== null && value.channels.includes(pending) && value.channels.length > 1 ? pending : null;
  const losing = confirming ? variantsWithContent(value, confirming) : [];
  const isLast = (channel: DeliveryChannel) => value.channels.length === 1 && value.channels[0] === channel;
  const errors = value.channels.length === 0 ? [] : issuesAt(validateMessagesDraft(value).issues, 'channels');

  const toggle = (channel: DeliveryChannel, on: boolean) => {
    if (on) {
      if (pending === channel) setPending(null);
      onChange(addChannel(value, channel));
    } else if (isLast(channel)) {
      return;
    } else if (variantsWithContent(value, channel).length > 0) {
      setPending(channel);
    } else {
      onChange(removeChannel(value, channel));
    }
  };

  const confirmRemoval = () => {
    if (confirming) onChange(removeChannel(value, confirming));
    setPending(null);
  };

  return (
    <fieldset className="stack-form" aria-label="Channels">
      <legend>Channels</legend>
      {GROUPS.map((group) => {
        const boxes = group.channels.map(({ channel, label }) => (
          <Checkbox key={channel} label={label} checked={value.channels.includes(channel)} disabled={readOnly || isLast(channel)} onCheckedChange={(on) => toggle(channel, on)} />
        ));
        return group.legend ? (
          <fieldset key={group.legend}>
            <legend>{group.legend}</legend>
            {boxes}
          </fieldset>
        ) : (
          boxes
        );
      })}
      {value.channels.length === 0 && (
        <p className="bb-field__error" role="alert">
          Choose at least one channel.
        </p>
      )}
      {value.channels.length === 1 && !readOnly && <p className="hint">A campaign needs at least one channel: add another one before removing {CHANNEL_LABELS[value.channels[0]!]}.</p>}
      {errors.map((message) => (
        <p key={message} className="bb-field__error" role="alert">
          {message}
        </p>
      ))}
      {confirming && (
        <div className="notice notice--warning" role="group" aria-label={`Remove ${CHANNEL_LABELS[confirming]}?`}>
          <p>
            Remove {CHANNEL_LABELS[confirming]}? Its content in {losing.map((v) => v.name).join(', ')} will be deleted.
          </p>
          <div className="actions">
            <Button variant="danger" onClick={confirmRemoval}>
              Remove channel
            </Button>
            <Button onClick={() => setPending(null)}>Keep</Button>
          </div>
        </div>
      )}
      {readOnly && <p className="hint">This revision is launched: its channels cannot change.</p>}
    </fieldset>
  );
}
